<script setup lang="ts">
import { computed } from "vue";
import {
  atIndexMaybe,
  isJust,
  isNothing,
  just,
  nothing,
} from "../../lib/maybe";
import { useEditorSessionStore } from "../../stores/useEditorSessionStore";
import { useProjectWorkspaceStore } from "../../stores/useProjectWorkspaceStore";
import { useOutlineEntitiesStore } from "../../stores/useOutlineEntitiesStore";
import {
  useStoryboardDragStore,
  type BoardDragSource,
} from "../../stores/useStoryboardDragStore";
import {
  unplacedItemDisplayName,
  unplacedItemShowsSynopsis,
  type UnplacedItem,
} from "../../lib/tauri/unplaced";
import { IconGripVertical } from "../ribbonIcons";

const session = useEditorSessionStore();
const workspace = useProjectWorkspaceStore();
const entities = useOutlineEntitiesStore();
const drag = useStoryboardDragStore();

const chapters = computed(() => session.chapters);

const TRAY_KEY = "unplaced-tray";

function isDragOver(key: string): boolean {
  // Pinia unwraps store refs; `dragOver` is already Maybe<string>.
  return isJust(drag.dragOver) && drag.dragOver.value === key;
}

function itemKey(item: UnplacedItem): string {
  return `unplaced-item:${item.kind}:${item.name}`;
}

function itemDragSource(item: UnplacedItem): BoardDragSource {
  return {
    key: itemKey(item),
    kind: item.kind === "scene" ? "unplaced-scene" : "unplaced-chapter",
    folder: nothing,
    actFolder: nothing,
    sceneFile: nothing,
    name: just(item.name),
    entryFile: nothing,
    momentId: nothing,
  };
}

/** Restoring an item (via keyboard, or a drop that doesn't land on an exact
 * slot) lands it at the end of the manuscript. Dragging it onto a precise
 * spot on the board itself is the richer, exact-slot path. */
async function restoreScene(name: string): Promise<void> {
  const last = atIndexMaybe(chapters.value, chapters.value.length - 1);
  if (isNothing(last)) {
    window.alert(
      "Create a chapter first, then restore this scene to the manuscript.",
    );
    return;
  }
  await workspace.onUnparkSceneIntoChapter(name, last.value.folder, nothing);
}

async function restoreChapter(name: string): Promise<void> {
  await workspace.onUnparkChapter(name, chapters.value.length);
}

function restore(item: UnplacedItem): void {
  if (item.kind === "scene") void restoreScene(item.name);
  else void restoreChapter(item.name);
}
</script>

<template>
  <nav class="zw-outline-nav zw-scroll" aria-label="Draft">
    <ul v-if="entities.unplaced.length > 0" class="zw-unplaced-list">
      <li
        v-for="item in entities.unplaced"
        :key="itemKey(item)"
        class="zw-unplaced-item"
        :class="{ 'zw-unplaced-item-drag-over': isDragOver(itemKey(item)) }"
        :data-board-key="itemKey(item)"
        data-board-kind="unplaced-item"
        :data-board-name="item.name"
        role="listitem"
        tabindex="0"
        aria-keyshortcuts="Enter"
        @keydown.enter="restore(item)"
      >
        <span
          class="zw-unplaced-item-grip"
          aria-hidden="true"
          title="Drag onto the board to restore to the manuscript — or select this item and press Enter"
          @pointerdown.stop="
            drag.onCardPointerDown(itemDragSource(item), $event)
          "
        >
          <IconGripVertical class="size-4" />
        </span>
        <div class="zw-unplaced-item-body">
          <span class="zw-unplaced-item-name">{{
            unplacedItemDisplayName(item)
          }}</span>
          <span v-if="item.kind === 'chapter'" class="zw-unplaced-item-meta">
            {{ item.scenes.length }}
            {{ item.scenes.length === 1 ? "scene" : "scenes" }}
          </span>
          <p
            v-if="unplacedItemShowsSynopsis(item)"
            class="zw-unplaced-item-synopsis"
          >
            {{ item.synopsis }}
          </p>
        </div>
      </li>
    </ul>
    <div v-else class="zw-outline-empty">
      <p>No drafts. Drag a card here from the board to move it to draft.</p>
    </div>
    <div
      class="zw-unplaced-tray"
      :class="{ 'zw-unplaced-tray-drag-over': isDragOver(TRAY_KEY) }"
      :data-board-key="TRAY_KEY"
      data-board-kind="unplaced-tray"
      aria-hidden="true"
    >
      Drop here to move to draft
    </div>
  </nav>
</template>

<style scoped>
.zw-outline-nav {
  flex: 1;
  min-height: 0;
  overflow: auto;
  padding: 0 var(--space-sm) var(--space-md);
  display: flex;
  flex-direction: column;
}

.zw-outline-empty {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: var(--space-sm);
  margin: var(--space-sm) var(--space-sm) 0;
  font-size: 0.9rem;
  line-height: 1.4;
  color: color-mix(in oklch, var(--text-muted) 85%, transparent);
}

.zw-outline-empty p {
  margin: 0;
}

.zw-unplaced-list {
  list-style: none;
  margin: 0;
  padding: var(--space-sm) 0 0;
  display: flex;
  flex-direction: column;
  gap: var(--space-sm);
}

.zw-unplaced-item {
  display: flex;
  align-items: flex-start;
  gap: var(--space-sm);
  padding: var(--space-sm);
  border: 1px solid var(--line);
  background: var(--bg-raised);
  border-radius: 0.375rem;
  cursor: default;
}

.zw-unplaced-item-drag-over {
  border-color: var(--color-zw-amber);
  border-style: dashed;
  background: color-mix(in oklch, var(--color-zw-amber) 8%, var(--bg-raised));
}

.zw-unplaced-item-grip {
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 1.25rem;
  height: 1.25rem;
  margin: -0.1rem 0 0 -0.2rem;
  color: var(--text-muted);
  cursor: grab;
  border-radius: 0.25rem;
  opacity: 0.55;
  transition: opacity var(--duration-fast) var(--ease-out-quart);
}

.zw-unplaced-item:hover .zw-unplaced-item-grip,
.zw-unplaced-item:focus-visible .zw-unplaced-item-grip {
  opacity: 1;
}

.zw-unplaced-item-grip:active {
  cursor: grabbing;
}

.zw-unplaced-item-body {
  display: flex;
  flex-direction: column;
  gap: 0.15rem;
  min-width: 0;
}

.zw-unplaced-item-name {
  font-size: 0.8125rem;
  color: var(--text);
}

.zw-unplaced-item-meta {
  font-size: 0.7rem;
  color: var(--text-muted);
}

.zw-unplaced-item-synopsis {
  margin: 0;
  font-size: 0.75rem;
  color: var(--text-muted);
}

/* The drop target for moving a board card to draft — quiet at rest (like the
 * board's own append-tail cells), highlighted only while something is
 * actually being dragged over it. */
.zw-unplaced-tray {
  flex: 1;
  min-height: 2.5rem;
  margin-top: var(--space-sm);
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px dashed transparent;
  border-radius: 0.375rem;
  font-size: 0.75rem;
  color: transparent;
  transition:
    border-color var(--duration-fast) var(--ease-out-quart),
    color var(--duration-fast) var(--ease-out-quart);
}

.zw-unplaced-tray-drag-over {
  border-color: var(--color-zw-amber);
  background: color-mix(in oklch, var(--color-zw-amber) 8%, transparent);
  color: color-mix(in oklch, var(--color-zw-amber) 70%, var(--text-muted));
}
</style>
